import React, { useCallback, useEffect, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalProps,
  ModalCloseButton,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  Progress,
  Box,
} from '@chakra-ui/react'
import { AddResidentInfo } from './add-resident-info'
import { AddPropertyAddressInfo } from './add-property-address-info'
import { FormProvider, useForm } from 'react-hook-form'
import { PropertyFormValues } from 'types/property.type'
import { useToast } from '@chakra-ui/react'
import { useCreateProjectMutation, useUpdateProjectMutation } from 'api/property'
import { useNavigate } from 'react-router-dom'
import { DevTool } from '@hookform/devtools'
import { useTranslation } from 'react-i18next'
import { NEW_PROPERTY } from 'features/property/property.i18n'
import { useProjectInformationNextButtonDisabled } from './hooks'
import { useAuth } from 'utils/auth-context'
import { useUserRolesSelector } from 'utils/redux-common-selectors'
import { useClients, useMarkets, useStates } from 'api/pc-projects'
import { Card } from 'components/card/card'

type AddPropertyFormProps = {
  onClose: () => void
  propertyData?: any
  projectId?: string | number | null
}

const AddPropertyForm: React.FC<AddPropertyFormProps> = ({ onClose, propertyData, projectId }) => {
  const { t } = useTranslation()
  const toast = useToast()
  const { isProjectCoordinator } = useUserRolesSelector()
  const { data } = useAuth()
  const user = data?.user

  const { mutate: savePropertyDetails, isLoading } = useCreateProjectMutation()
  const { mutate: updateProperty } = useUpdateProjectMutation()

  const [tabIndex, setTabIndex] = useState(0)
  const [isDuplicateAddress, setIsDuplicateAddress] = useState(false)
  const navigate = useNavigate()

  const setNextTab = () => {
    setTabIndex(tabIndex + 1)
  }

  const { stateSelectOptions } = useStates()
  const { marketSelectOptions, isLoading: isLoadingMarkets } = useMarkets()
  const { clientSelectOptions, isLoading: isLoadingClients } = useClients()

  const formReturn = useForm<PropertyFormValues>({
    defaultValues: {
      client: null,
      name: '',
      streetAddress: '',
      city: '',
      state: null,
      zipCode: '',
      gateCode: '',
      lockBoxCode: '',
      hoaPhone: '',
      hoaEmailAddress: '',
      market: null,
      propertyNotes: '',
      residents: [
        {
          firstName: '',
          lastName: '',
          emailAddress: '',
          phoneNumber: '',
        },
      ],
    },
  })

  const isEdit = !!propertyData?.id;

  useEffect(() => {
    if (!propertyData || !clientSelectOptions || !marketSelectOptions || !stateSelectOptions) return
    const formValues = {
      ...propertyData,
      client: clientSelectOptions?.find(c => c.value === propertyData?.clientId),
      market: marketSelectOptions?.find(m => m.value === propertyData?.marketId),
      state: stateSelectOptions?.find(s => s.value === propertyData?.state),
    }

    formReturn.reset(formValues)
  }, [propertyData, isLoadingClients, isLoadingMarkets])

  const isProjectInfoNextButtonDisabled = useProjectInformationNextButtonDisabled(
    formReturn.control as any,
    formReturn?.formState?.errors,
  )

  const { handleSubmit } = formReturn

  const onSubmit = useCallback(
    async values => {
      const residents = values.residents

      if (propertyData) {
        const formValues = {
          ...propertyData,
          streetAddress: values.streetAddress,
          city: values.city,
          marketId: values.market?.value,
          state: values.state?.value,
          zipCode: values.zipCode,
          residents: residents,
          propertyNotes: values.propertyNotes,
          clientName: values.client?.label,
          clientId: values.client?.value,
          unitNo: values.unitNo,
          projectId: projectId ? Number(projectId) : null
        }

        updateProperty(formValues, {
          onSuccess(response) {
            onClose()
            toast({
              title: 'Update Property.',
              description: 'Property has been updated successfully.',
              status: 'success',
              isClosable: true,
              position: 'top-left',
            })
            window.location.reload();
          },
          onError(error: any) {
            toast({
              title: error?.title || 'Error while updating Property.',
              description: error?.message || 'Something went wrong.',
              status: 'error',
              isClosable: true,
              position: 'top-left',
            })
          },
        })
      } else {
        const newPropertyPayload = {
          streetAddress: values.streetAddress,
          city: values.city,
          marketId: values.market?.value,
          state: values.state?.value,
          zipCode: values.zipCode,
          name: 'test',
          gateCode: null,
          lockBoxCode: null,
          hoaPhone: null,
          hoaEmailAddress: null,
          residents: residents,
          propertyNotes: values.propertyNotes,
          clientName: values.client?.label,
          clientId: values.client?.value,
          unitNo: values.unitNo,
          status: 'ACTIVE',
        }
        savePropertyDetails(newPropertyPayload, {
          onSuccess(response) {
            const project = response?.data
            const projectId = project?.id
            const projectCordinatorId = project?.projectCordinatorId

            toast({
              title: 'Property Details',
              description: `New property has been created successfully with property id: ${projectId}`,
              status: 'success',
              duration: 9000,
              isClosable: true,
              position: 'top-left',
            })
            onClose()

            // In case project coordinator created a new project for other user
            // than it should be redirected to project details page
            if (isProjectCoordinator && user?.id !== projectCordinatorId) return

            navigate(`/property-details/${projectId}`)
          },
          onError(error: any) {
            toast({
              title: 'Property Details',
              description: (error.title as string) ?? 'Unable to save property.',
              status: 'error',
              duration: 9000,
              isClosable: true,
              position: 'top-left',
            })
          },
        })
      }
    },
    [savePropertyDetails, updateProperty],
  )

  return (
    <Box>
      {isLoading && (
        <Progress size="xs" isIndeterminate position="absolute" top="60px" left="0" width="100%" aria-label="loading" />
      )}
      <FormProvider {...formReturn}>
        <form onSubmit={handleSubmit(onSubmit)} id="newProjectForm">
          <Tabs
            colorScheme="brand"
            variant="enclosed"
            index={tabIndex}
            onChange={index => setTabIndex(index)}
            mt="0"
            width="98%"
            ml="0.5"
          >
            <TabList color="#4A5568" mx="3">
              <Tab>{t(`${NEW_PROPERTY}.propertyInformation`)}</Tab>
              <Tab isDisabled={isProjectInfoNextButtonDisabled && !isEdit}>{t(`${NEW_PROPERTY}.residentInformation`)}</Tab>
            </TabList>
            <TabPanels mt="31px" h="100%">
              <TabPanel p="0px" h="100%">
                <Card
                  ml="3"
                  mt="-3.6%"
                  boxShadow="0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)"
                  borderTopLeftRadius={0}
                >
                  <AddPropertyAddressInfo
                    isLoading={false}
                    setNextTab={setNextTab}
                    onClose={onClose}
                    isDuplicateAddress={isDuplicateAddress}
                    setIsDuplicateAddress={setIsDuplicateAddress}
                    stateSelectOptions={stateSelectOptions}
                    marketSelectOptions={marketSelectOptions}
                    clientSelectOptions={clientSelectOptions}
                    isEdit={isEdit}
                  />
                </Card>
              </TabPanel>
              <TabPanel p="0px" h="100%">
                <Card
                  ml="3"
                  mt="-3.6%"
                  boxShadow="0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)"
                  borderTopLeftRadius={0}
                >
                  <AddResidentInfo setNextTab={setNextTab} onClose={onClose} />
                </Card>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </form>

        <DevTool control={formReturn.control} />
      </FormProvider>
    </Box>
  )
}

type CustomModalProps = Pick<ModalProps, 'isOpen' | 'onClose'>
type AddNewPropertyProps = CustomModalProps
type UpdatePropertyProps = CustomModalProps & {
  propertyData: any
  projectId?: string | number | null
}

export const AddNewPropertyModal: React.FC<AddNewPropertyProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      variant="custom"
      isCentered
      closeOnEsc={true}
      closeOnOverlayClick={false}
    >
      <ModalOverlay>
        <ModalContent bg="#F2F3F4">
          <ModalHeader bg="#fff" border="1px solid #E2E8F0">
            {t(`${NEW_PROPERTY}.title`)}
          </ModalHeader>
          <ModalCloseButton _focus={{ outline: 'none' }} />
          <ModalBody px="0">
            <AddPropertyForm onClose={onClose} />
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  )
}

export const UpdatePropertyModal: React.FC<UpdatePropertyProps> = ({ isOpen, onClose, propertyData, projectId }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      variant="custom"
      isCentered
      closeOnEsc={true}
      closeOnOverlayClick={false}
    >
      <ModalOverlay>
        <ModalContent bg="#F2F3F4">
          <ModalHeader bg="#fff" border="1px solid #E2E8F0">Update Property</ModalHeader>
          <ModalCloseButton _focus={{ outline: 'none' }} />
          <ModalBody px="0">
            <AddPropertyForm onClose={onClose} propertyData={propertyData} projectId={projectId} />
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  )
}
